import { AxiosRequestConfig } from 'axios'
import { KibsiClientParams } from './params'
import {
    ApplicationKibsiClient,
    DashboardKibsiClient,
    DeploymentKibsiClient,
    DetectorKibsiClient,
    EdgeDeviceKibsiClient,
    EventKibsiClient,
    FloorPlanKibsiClient,
    HistoryKibsiClient,
    IntegrationKibsiClient,
    RealtimeDataKibsiClient,
    SiteKibsiClient,
    StreamKibsiClient,
    SubscriptionKibsiClient,
    TagKibsiClient,
    TenantKibsiClient,
    UserKibsiClient,
} from './services'
import { TimeKibsiClient } from './time'

export type IKibsiClient<T = unknown> = {
    new (params: KibsiClientParams): T
}

/**
 * SDK exposing entity clients.
 */
export class KibsiClientSDK {
    static tokenAuthRequestConfig(token: { access_token: string }): AxiosRequestConfig {
        return {
            headers: {
                Authorization: `Bearer ${token.access_token}`,
            },
        }
    }

    private readonly clients = new Map<IKibsiClient, unknown>()

    protected readonly clientParams: KibsiClientParams

    constructor(sdkParams: KibsiClientParams) {
        this.clientParams = {
            ...sdkParams,
        }

        if (this.clientParams.basePathTemplate.indexOf('{service}') === -1) {
            throw new Error('basePathTemplate must include {service} placeholder.')
        }
    }

    tenants(): TenantKibsiClient {
        return this.cacheClient(TenantKibsiClient)
    }

    sites(): SiteKibsiClient {
        return this.cacheClient(SiteKibsiClient)
    }

    users(): UserKibsiClient {
        return this.cacheClient(UserKibsiClient)
    }

    integrations(): IntegrationKibsiClient {
        return this.cacheClient(IntegrationKibsiClient)
    }

    deployments(): DeploymentKibsiClient {
        return this.cacheClient(DeploymentKibsiClient)
    }

    events(): EventKibsiClient {
        return this.cacheClient(EventKibsiClient)
    }

    subscriptions(): SubscriptionKibsiClient {
        return this.cacheClient(SubscriptionKibsiClient)
    }

    time(): TimeKibsiClient {
        return this.cacheClient(TimeKibsiClient)
    }

    streams(): StreamKibsiClient {
        return this.cacheClient(StreamKibsiClient)
    }

    detectors(): DetectorKibsiClient {
        return this.cacheClient(DetectorKibsiClient)
    }

    applications(): ApplicationKibsiClient {
        return this.cacheClient(ApplicationKibsiClient)
    }

    realtimeData(): RealtimeDataKibsiClient {
        return this.cacheClient(RealtimeDataKibsiClient)
    }

    history(): HistoryKibsiClient {
        return this.cacheClient(HistoryKibsiClient)
    }

    edgeDevice(): EdgeDeviceKibsiClient {
        return this.cacheClient(EdgeDeviceKibsiClient)
    }

    floorPlan(): FloorPlanKibsiClient {
        return this.cacheClient(FloorPlanKibsiClient)
    }

    dashboard(): DashboardKibsiClient {
        return this.cacheClient(DashboardKibsiClient)
    }

    tag(): TagKibsiClient {
        return this.cacheClient(TagKibsiClient)
    }

    protected cacheClient<T, C extends IKibsiClient<T>>(Client: C): T {
        if (!this.clients.has(Client)) {
            this.clientParams.logger?.debug(`Initializing client for ${Client.name}.`)
            this.clients.set(Client, new Client(this.clientParams))
        }
        return this.clients.get(Client) as T
    }
}
